import React, { useState, useEffect, useCallback, FormEvent, useRef } from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden, TextField, CircularProgress } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import firebase from 'firebase/app';
import 'firebase/database';

import IntroContainer from '../IntroContainer/IntroContainer';
import LocalVideoPreview from '../PreJoinScreens/DeviceSelectionScreen/LocalVideoPreview/LocalVideoPreview';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useChatContext from '../../hooks/useChatContext/useChatContext';
import EnhancedToggleAudioButton from '../Buttons/EnhancedToggleAudioButton/EnhancedToggleAudioButton';
import EnhancedToggleVideoButton from '../Buttons/EnhancedToggleVideoButton/EnhancedToggleVideoButton';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  marginTop: {
    marginTop: '1em',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '1em 2.5em 0 2.5em',
      padding: '0',
    },
  },
  mobileButton: {
    padding: '6px 12px', // Reduced from 10px 20px
    margin: '0 auto',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '120px',
    fontSize: '14px',
    borderRadius: '8px',
    minHeight: '45px', // Add consistent height
    border: '2px solid #ddd',
    ['& .MuiButton-startIcon']: {
      margin: '0px !important',
      padding: '0px !important',
    },
  },
  nameInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#ddd',
      },
      '&:hover fieldset': {
        borderColor: '#bbb',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  },
  statusMessage: {
    textAlign: 'center',
    margin: '0.5em 0',
    minHeight: '1.5em', // Reserve space for error messages
  },
  waitingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '1em 0',
    textAlign: 'center',
  },
  toggleButtonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: '0.75em',
    [theme.breakpoints.down('sm')]: {
      gap: '0.5em',
    },
  },
  toggleButtonDesktop: {
    flex: 1,
    border: '2px solid #ddd',
    borderRadius: '8px',
    padding: '8px 12px', // Reduced from 12px 16px
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '45px', // Add consistent height
    '& .MuiButton-startIcon': {
      marginLeft: '8px',
    },
    '&:hover': {
      borderColor: '#bbb',
    },
  },
  joinButtonContainer: {
    marginTop: 'auto',
    // paddingTop: '2em',
  },
  joinButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: '8px',
    textTransform: 'none',
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: '#999',
    },
  },
  rightPanelContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: '1em',
      height: 'auto',
      //   minHeight: '350px',
    },
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5em',
  },
  controlsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
  },
}));

interface GuestJoinScreenProps {}

export default function GuestJoinScreen({}: GuestJoinScreenProps) {
  const classes = useStyles();
  const location = useLocation();

  // Extract uid from query parameters instead of URL params
  const queryParams = new URLSearchParams(location.search);
  const uid = queryParams.get('uid');

  // State management
  const [guestName, setGuestName] = useState<string>('');
  const [roomName, setRoomName] = useState<string>('');

  // Join flow states
  const [isRequestingJoin, setIsRequestingJoin] = useState(false);
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [firebaseListener, setFirebaseListener] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const tokenRef = useRef<string | null>(null);

  // Hooks
  const {
    getAudioAndVideoTracks,
    connect: videoConnect,
    isAcquiringLocalTracks,
    isConnecting,
    localTracks,
  } = useVideoContext();
  const { connect: chatConnect } = useChatContext();

  // Audio/Video toggle hooks
  //   const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
  //   const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
  //   const { hasVideoInputDevices } = useDevices();
  //   const lastClickTimeRef = useRef(0);

  // Audio/Video state
  //   const hasAudioTrack = localTracks.some(track => track.kind === 'audio');

  const disableButtons = isRequestingJoin || isAcquiringLocalTracks || isConnecting || isWaitingForApproval;
  const disableJoinButton = disableButtons || !guestName.trim();

  // Custom toggle functions
  //   const handleToggleVideo = useCallback(() => {
  //     if (Date.now() - lastClickTimeRef.current > 500) {
  //       lastClickTimeRef.current = Date.now();
  //       toggleVideoEnabled();
  //     }
  //   }, [toggleVideoEnabled]);

  //   const handleToggleAudio = useCallback(() => {
  //     toggleAudioEnabled();
  //   }, [toggleAudioEnabled]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (uid) {
      localStorage.setItem('avFilter', 'video');
      // ADD THIS LINE to fix video auto-enable:
      const cookies = new Cookies();
      cookies.set('callingType', 'video', { path: '/' });

      console.log('Guest joining with UID:', uid);
    }
  }, [uid]);

  useEffect(() => {
    return () => {
      // Cleanup Firebase listener when component unmounts
      if (firebaseListener) {
        const database = firebase.database();
        const ref = database.ref(firebaseListener);
        ref.off();
      }
    };
  }, [firebaseListener]);

  // Handle approved join
  const handleApprovedJoin = useCallback(() => {
    console.log('=== handleApprovedJoin called ===');
    console.log('Token exists:', !!token);
    console.log('Token value:', token);
    console.log('Current isWaitingForApproval:', isWaitingForApproval);

    if (!token) {
      console.log('❌ No token available, returning early');
      return;
    }

    console.log('✅ Guest approved, joining room...');
    setIsWaitingForApproval(false);

    console.log('🎥 About to call videoConnect with token:', token);

    // Connect to video room - this will change roomState from 'disconnected' to 'connected'
    try {
      videoConnect(token);
      console.log('✅ videoConnect called successfully');
    } catch (error) {
      console.error('❌ Error calling videoConnect:', error);
    }

    // Connect to chat if enabled
    if (process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true') {
      try {
        chatConnect(token);
        console.log('✅ chatConnect called successfully');
      } catch (error) {
        console.error('❌ Error calling chatConnect:', error);
      }
    } else {
      // console.log('💬 Chat disabled via environment variable');
    }
  }, [token, videoConnect, chatConnect, isWaitingForApproval]);

  // Firebase listener setup (placeholder for when backend provides details)
  const setupFirebaseListener = useCallback(
    (listenerNode: string) => {
      console.log('🔥 Setting up Firebase listener for node:', listenerNode);

      const firebaseConfig = {
        apiKey: 'AIzaSyDFGCzE5oFdq0sLFoJwad8nEGr7JLlaDfk',
        authDomain: 'focusmate-28bf2.firebaseapp.com',
        databaseURL: 'https://focusmate-28bf2-default-rtdb.europe-west1.firebasedatabase.app',
        projectId: 'focusmate-28bf2',
        storageBucket: 'focusmate-28bf2.firebasestorage.app',
        messagingSenderId: '414417815152',
        appId: '1:414417815152:web:4741035e90f745583516b5',
        measurementId: 'G-8X8KEDQ2EF',
      };

      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
        console.log('🔥 Firebase initialized');
      } else {
        console.log('🔥 Firebase already initialized');
      }

      const database = firebase.database();
      const ref = database.ref(listenerNode);

      const listener = ref.on('value', snapshot => {
        const data = snapshot.val();
        console.log('🔥 Firebase data received:', data);

        if (data && data.approved === true) {
          console.log('✅ Guest approved! Getting current token...');

          const currentToken = tokenRef.current;
          console.log('Current token from ref:', currentToken);

          if (!currentToken) {
            console.log('❌ No token available in ref, cannot join');
            setJoinError('Token not available. Please try requesting again.');
            setIsWaitingForApproval(false);
            return;
          }

          setIsWaitingForApproval(false);

          try {
            videoConnect(currentToken);
          } catch (error) {
            console.error('❌ Error calling videoConnect:', error);
          }

          if (process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true') {
            try {
              chatConnect(currentToken);
            } catch (error) {
              console.error('❌ Error calling chatConnect:', error);
            }
          }
        } else if (data && data.approved === false) {
          console.log('❌ Guest rejected');
          setJoinError('Your request to join has been declined.');
          setIsWaitingForApproval(false);
        }
      });

      return () => {
        console.log('🔥 Cleaning up Firebase listener');
        ref.off('value', listener);
      };
    },
    [videoConnect, chatConnect]
  );

  // Handle guest join request
  const handleGuestJoinRequest = async () => {
    if (!guestName.trim() || !uid) {
      console.log('❌ Missing guestName or uid:', { guestName, uid });
      return;
    }

    console.log('🚀 Starting guest join request...');
    console.log('Guest name:', guestName);
    console.log('UID:', uid);

    setIsRequestingJoin(true);
    setJoinError(null);

    try {
      // API call for guest join request with URL params
      const endpoint = `${process.env.REACT_APP_ENDPOINT}/twillio/asktojoin?uuid=${encodeURIComponent(
        uid
      )}&name=${encodeURIComponent(guestName.trim())}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
      });

      const data = await response.json();
      console.log('📡 API response data:', data);

      if (response.ok && data.token && data.admitListenNode) {
        console.log('✅ API call successful');
        console.log('Token received:', data.token);
        console.log('admitListenNode:', data.admitListenNode);
        console.log('roomName:', data.roomName);

        // Store token and setup Firebase listener
        setToken(data.token);
        setFirebaseListener(data.admitListenNode);
        setRoomName(data.roomName || 'Meeting Room');
        localStorage.setItem('avFilter', 'video');

        // Setup Firebase listener for approval
        setupFirebaseListener(data.admitListenNode);

        setIsRequestingJoin(false);
        setIsWaitingForApproval(true);
      } else {
        console.error('❌ API call failed:', data);
        throw new Error(data.message || 'Failed to request join');
      }
    } catch (error) {
      console.error('❌ Guest join request failed:', error);
      setJoinError(error instanceof Error ? error.message : 'Failed to request join. Please try again.');
      setIsRequestingJoin(false);
    }
  };

  // Handle form submission
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleGuestJoinRequest();
  };

  // Loading state while connecting
  if (isConnecting) {
    return (
      <IntroContainer>
        <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
          <div>
            <CircularProgress variant="indeterminate" />
          </div>
          <div>
            <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Joining Meeting
            </Typography>
          </div>
        </Grid>
      </IntroContainer>
    );
  }

  // Waiting for approval state
  if (isWaitingForApproval) {
    return (
      <IntroContainer>
        <Typography variant="h5" className={classes.gutterBottom}>
          Waiting for Approval
        </Typography>

        <Grid container justifyContent="center">
          <Grid item md={7} sm={12} xs={12}>
            <div className={classes.localPreviewContainer}>
              <LocalVideoPreview identity={guestName} />
            </div>
            <div className={classes.mobileButtonBar}>
              <Hidden mdUp>
                <div className={classes.toggleButtonContainer}>
                  {/* <CustomToggleAudioButton className={classes.mobileButton} disabled={false} />
                  <CustomToggleVideoButton className={classes.mobileButton} disabled={false} /> */}

                  <EnhancedToggleAudioButton
                    className={classes.mobileButton}
                    disabled={disableButtons}
                    buttonText={false}
                  />
                  <EnhancedToggleVideoButton
                    className={classes.mobileButton}
                    disabled={disableButtons}
                    buttonText={false}
                  />
                </div>
              </Hidden>
            </div>
          </Grid>
          <Grid item md={5} sm={12} xs={12}>
            <div className={classes.rightPanelContainer}>
              {/* Desktop Audio/Video Controls */}
              <Hidden smDown>
                <div className={classes.toggleButtonContainer}>
                  {/* <CustomToggleAudioButton className={classes.toggleButtonDesktop} disabled={false} />
                  <CustomToggleVideoButton className={classes.toggleButtonDesktop} disabled={false} /> */}

                  <EnhancedToggleAudioButton
                    className={classes.toggleButtonDesktop}
                    disabled={disableButtons}
                    buttonText={false}
                  />
                  <EnhancedToggleVideoButton
                    className={classes.toggleButtonDesktop}
                    disabled={disableButtons}
                    buttonText={false}
                  />
                </div>
              </Hidden>

              <div className={classes.waitingContainer}>
                <CircularProgress variant="indeterminate" size={60} />
                <Typography variant="body1" className={classes.statusMessage}>
                  Your request to join "{roomName}" has been sent.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Please wait for the host to approve your request.
                </Typography>
                {joinError && (
                  <Typography variant="body2" color="error" className={classes.statusMessage}>
                    {joinError}
                  </Typography>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </IntroContainer>
    );
  }

  // Main guest join form
  return (
    <IntroContainer>
      <Typography variant="h5" className={classes.gutterBottom}>
        Join as Guest
      </Typography>

      <Grid container justifyContent="center">
        <Grid item md={7} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={guestName || 'Guest'} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              <div className={classes.toggleButtonContainer}>
                {/* <CustomToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
                <CustomToggleVideoButton className={classes.mobileButton} disabled={disableButtons} /> */}

                <EnhancedToggleAudioButton
                  className={classes.mobileButton}
                  disabled={disableButtons}
                  buttonText={false}
                />
                <EnhancedToggleVideoButton
                  className={classes.mobileButton}
                  disabled={disableButtons}
                  buttonText={false}
                />
              </div>
            </Hidden>
          </div>
        </Grid>

        <Grid item md={5} sm={12} xs={12}>
          <div className={classes.rightPanelContainer}>
            <div className={classes.formSection}>
              {/* Name Input Field */}
              <form onSubmit={handleSubmit}>
                <TextField
                  className={classes.nameInput}
                  label="Enter your name"
                  variant="outlined"
                  value={guestName}
                  onChange={e => {
                    const value = e.target.value;
                    // Only allow letters, spaces, hyphens, and apostrophes
                    const validNamePattern = /^[a-zA-Z\s\-']*$/;

                    // Check if input matches pattern and is within length limit
                    if (validNamePattern.test(value) && value.length <= 20) {
                      setGuestName(value);
                    }
                  }}
                  disabled={disableButtons}
                  required
                  inputProps={{
                    maxLength: 20,
                    pattern: "[a-zA-Z\\s\\-']*",
                    title: 'Please enter a valid name (letters, spaces, hyphens, and apostrophes only)',
                  }}
                  error={guestName.length > 0 && !/^[a-zA-Z\s\-']+$/.test(guestName)}
                  helperText={
                    guestName.length > 0 && !/^[a-zA-Z\s\-']+$/.test(guestName)
                      ? 'Please use only letters, spaces, hyphens, and apostrophes'
                      : `${guestName.length}/20 characters`
                  }
                />
              </form>

              {/* Desktop Audio/Video Controls */}
              <Hidden smDown>
                <div className={classes.controlsSection}>
                  <div className={classes.toggleButtonContainer}>
                    {/* <CustomToggleAudioButton className={classes.toggleButtonDesktop} disabled={disableButtons} />
                    <CustomToggleVideoButton className={classes.toggleButtonDesktop} disabled={disableButtons} /> */}

                    <EnhancedToggleAudioButton
                      className={classes.toggleButtonDesktop}
                      disabled={disableButtons}
                      buttonText={false}
                    />
                    <EnhancedToggleVideoButton
                      className={classes.toggleButtonDesktop}
                      disabled={disableButtons}
                      buttonText={false}
                    />
                  </div>
                </div>
              </Hidden>
            </div>

            {/* Error Message - Fixed height container */}
            <div className={classes.statusMessage}>
              {joinError && (
                <Typography variant="body2" color="error">
                  {joinError}
                </Typography>
              )}
            </div>

            {/* Join Button - Always at bottom */}
            <div className={classes.joinButtonContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGuestJoinRequest}
                disabled={disableJoinButton}
                startIcon={isRequestingJoin ? <CircularProgress size={20} color="inherit" /> : null}
                fullWidth
                className={classes.joinButton}
              >
                {isRequestingJoin ? 'Requesting...' : 'Ask to Join'}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>
    </IntroContainer>
  );
}
